/**
 * Sound, and the moments worth celebrating.
 *
 * ── Synthesized, not recorded ──────────────────────────────────────────────
 *
 * Every sound here is built with Web Audio at the moment it plays, the same
 * way FlowMentum does it: no audio files to ship, nothing to cache, and the
 * same sound in the app and on the web. WKWebView runs Web Audio, so the iOS
 * shell needs no new build for any of it.
 *
 * ── Three weights, used for what they mean ─────────────────────────────────
 *
 *   tick       any button. Barely there, so a hundred taps never grate.
 *   done       something was sent or saved: a message, a profile, a move.
 *   celebrate  a moment that matters to somebody's week: an application
 *              sent, a job posted, a business registered, someone hired.
 *              A rising chime, sparks, and a card that says well done.
 *
 * If everything celebrates, nothing does — so `celebrate` stays on the short
 * list above and does not spread to every form.
 *
 * ── Off is one tap ─────────────────────────────────────────────────────────
 *
 * The speaker in the header. Remembered per device in localStorage, because
 * whether a phone makes noise is about where the phone is, not who owns it.
 */

const KEY = 'worx.sound'

let ctx: AudioContext | null = null

export function soundOn(): boolean {
  try {
    return localStorage.getItem(KEY) !== 'off'
  } catch {
    return true
  }
}

export function setSoundOn(on: boolean): void {
  try {
    localStorage.setItem(KEY, on ? 'on' : 'off')
  } catch {
    // Private mode: it still works for this visit.
  }
  if (on) done()
}

function audio(): AudioContext | null {
  if (!soundOn()) return null
  try {
    ctx ??= new AudioContext()
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

function tone(freq: number, at: number, dur: number, gain: number, type: OscillatorType = 'sine') {
  const a = audio()
  if (!a) return
  const t = a.currentTime + at
  const o = a.createOscillator()
  const g = a.createGain()
  o.type = type
  o.frequency.setValueAtTime(freq, t)
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(gain, t + 0.008)
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
  o.connect(g).connect(a.destination)
  o.start(t)
  o.stop(t + dur + 0.05)
}

/** A button. A soft wooden tap, short enough to sit under a finger. */
export function tick(): void {
  tone(1400, 0, 0.05, 0.025, 'triangle')
}

/** Sent or saved: two quick notes, up a fourth. */
export function done(): void {
  tone(784, 0, 0.18, 0.05, 'triangle')
  tone(1046.5, 0.08, 0.28, 0.045, 'triangle')
}

function fanfare(): void {
  // C major, climbing, with a bright top that rings on.
  tone(523.3, 0, 0.5, 0.05, 'triangle')
  tone(659.3, 0.09, 0.5, 0.05, 'triangle')
  tone(784, 0.18, 0.6, 0.05, 'triangle')
  tone(1046.5, 0.3, 1.2, 0.05)
  tone(1568, 0.36, 1.0, 0.02)
}

export interface Moment {
  title: string
  line: string
}

/** The overlay listens for this; see Celebration.tsx. */
export const CELEBRATE = 'worx:celebrate'

/** A moment that matters: sound, sparks and a card. */
export function celebrate(moment: Moment): void {
  fanfare()
  try {
    navigator.vibrate?.([12, 60, 24])
  } catch {
    // Not on this device.
  }
  window.dispatchEvent(new CustomEvent<Moment>(CELEBRATE, { detail: moment }))
}

/**
 * The tick on every button, wired once at the root rather than in a hundred
 * onClick handlers. Pointer-down, not click, so the sound lands with the
 * finger rather than after it lifts.
 */
export function listenForTaps(): () => void {
  const onDown = (e: PointerEvent) => {
    const button = (e.target as Element | null)?.closest('button')
    if (button && !button.disabled) tick()
  }
  /*
   * Safari, and every browser on an iPhone or iPad, keeps Web Audio silent
   * until it is started inside a click or touchend — pointerdown does not
   * count there. So the first real tap starts it and plays one silent sample,
   * and from then on every sound is allowed.
   */
  const unlock = () => {
    const a = audio()
    if (!a) return
    const src = a.createBufferSource()
    src.buffer = a.createBuffer(1, 1, 22050)
    src.connect(a.destination)
    src.start(0)
  }
  document.addEventListener('pointerdown', onDown, { passive: true })
  document.addEventListener('click', unlock, { passive: true })
  document.addEventListener('touchend', unlock, { passive: true })
  return () => {
    document.removeEventListener('pointerdown', onDown)
    document.removeEventListener('click', unlock)
    document.removeEventListener('touchend', unlock)
  }
}
